import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import type {
  FormSchemaV2,
  LegacyFormSchema,
  FormField,
  AppRole,
} from "@livingsyncs/types";

// Re-exported so existing imports from this hook continue to work
export type { FormField };

/** @deprecated Use FormSchemaV2 from @livingsyncs/types for new templates. */
export interface FormSchema {
  fields: FormField[];
}

export interface FormTemplate {
  id: string;
  name: string;
  description: string | null;
  version: number;
  // US-028: "external_provider" is filled by off-site doctors / dentists via
  // the grant flow (US-029). The signature_mode / requires_npi / requires_license
  // fields below are surfaced only when audience equals this value; the
  // backend mapper resets them to safe defaults for the other three.
  // US-043: "external_state_form" is a state-issued PDF linked by URL — the
  // submission_mode + external_form_* fields below describe the upload-only
  // workflow used by the resident Required Forms section.
  audience:
    | "resident"
    | "staff"
    | "facility"
    | "external_provider"
    | "external_state_form";
  signature_mode?:
    "typed_attestation" | "drawn_signature" | "wet_signature_upload";
  requires_npi?: boolean;
  requires_license?: boolean;
  // US-043: external state form metadata. Required when audience =
  // 'external_state_form'; null otherwise. The backend mapper enforces
  // this and the API never serves a populated URL on other audiences.
  external_form_url?: string | null;
  external_form_label?: string | null;
  external_form_revision?: string | null;
  submission_mode?: "schema_driven" | "upload_only";
  frequency:
    "daily" | "weekly" | "monthly" | "quarterly" | "annually" | "on_demand";
  /**
   * US-116: optional role_key that completes this form's derived task. Null =
   * every capable viewer (form-submission:write holders). The backend rejects a
   * role that can't submit forms.
   */
  required_role?: AppRole | null;
  /** v2 schema (section-based) or legacy flat schema. Use isFormSchemaV2() to discriminate. */
  schema: FormSchemaV2 | LegacyFormSchema;
  html_template?: string; // Legacy — no longer written by the form builder
  status: "draft" | "published" | "archived";
  is_latest_version: boolean;
  parent_template_id: string | null;
  group_home_id: string | null;
  facility_id: string | null;
  created_by: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * True when this template is referenced by a compliance bundle. The bundle
   * owns the template's identity, so the builder locks name/description/
   * audience/frequency (read-only) and the AI generator only writes the body.
   * Present on the admin GET (list + detail) and on GET /api/form-templates
   * (powers the group-home Forms page's Regular/Bundle tabs); absent on
   * GET /api/form-templates/usable and the single-template detail route.
   */
  in_rule_bundle?: boolean;

  /**
   * The compliance bundle that owns this template's version chain, or null
   * when not bundle-managed. Powers the admin "Bundle Forms" tab filters and
   * the group-home Forms page's Bundle Forms tab (cross-checked against
   * useFacilityAdoptions there). Present on the admin LIST response and on
   * GET /api/form-templates; absent on the detail route and /usable.
   */
  bundle?: {
    id: string;
    code: string;
    display_name: string;
    state: string | null;
    specialty: string | null;
  } | null;
  /**
   * LIVINGSYNCS-BACKEND-16: assignment counts embedded on the list response
   * so row badges don't each fetch GET /form-templates/:id/assignments (N+1).
   * Present on GET /api/form-templates; absent elsewhere.
   */
  assignment_summary?: { group_homes: number; facilities: number };
}

// Shared by the admin Bundle Forms tab and the group-home Forms page's Bundle
// Forms tab. Keys on both `bundle` and the older `in_rule_bundle` boolean — a
// frontend-before-backend deploy (or a cached pre-change list response) has
// no `bundle` object yet, and keying on it alone would silently dump every
// bundle-managed template into "Regular Forms".
export function isBundleManaged(t: FormTemplate): boolean {
  return !!t.bundle || t.in_rule_bundle === true;
}

interface FormTemplateFilters {
  status?: string;
  audience?: string;
  facility_id?: string;
  group_home_id?: string;
  latest_only?: boolean;
  resident_id?: string; // For user-facing API
  employee_id?: string; // For user-facing API
}

/**
 * Get form templates (admin API)
 */
export function useAdminFormTemplates(filters?: FormTemplateFilters) {
  return useQuery<FormTemplate[]>({
    queryKey: ["admin", "form-templates", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append("status", filters.status);
      if (filters?.audience) params.append("audience", filters.audience);
      if (filters?.facility_id)
        params.append("facility_id", filters.facility_id);
      if (filters?.group_home_id)
        params.append("group_home_id", filters.group_home_id);
      if (filters?.latest_only) params.append("latest_only", "true");

      const { data } = await api.get(
        `/admin/form-templates?${params.toString()}`,
      );
      return data.data;
    },
  });
}

/**
 * Get single form template (admin API)
 */
export function useAdminFormTemplate(id: string) {
  return useQuery<FormTemplate>({
    queryKey: ["admin", "form-templates", id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/form-templates/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

/**
 * Get ALL published form templates (for browsing/previewing)
 * Use this on the main /forms list page where all forms should be visible
 */
export function useFormTemplates(filters?: FormTemplateFilters) {
  return useQuery<FormTemplate[]>({
    queryKey: ["form-templates", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.audience) params.append("audience", filters.audience);
      if (filters?.resident_id)
        params.append("resident_id", filters.resident_id);
      if (filters?.employee_id)
        params.append("employee_id", filters.employee_id);

      const { data } = await api.get(`/form-templates?${params.toString()}`);
      return data.data;
    },
  });
}

/**
 * Get USABLE form templates (assigned to user's facilities/group homes)
 * Use this on context-specific pages (resident/employee/facility detail pages)
 * where forms can actually be filled out
 */
export function useUsableFormTemplates(filters?: FormTemplateFilters) {
  return useQuery<FormTemplate[]>({
    queryKey: ["form-templates", "usable", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.audience) params.append("audience", filters.audience);
      if (filters?.facility_id)
        params.append("facility_id", filters.facility_id);
      if (filters?.resident_id)
        params.append("resident_id", filters.resident_id);
      if (filters?.employee_id)
        params.append("employee_id", filters.employee_id);

      const { data } = await api.get(
        `/form-templates/usable?${params.toString()}`,
      );
      return data.data;
    },
  });
}

/**
 * Create form template
 */
export function useCreateFormTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormBuilderPage catch is sole caller.
    meta: { errorFallback: "Failed to save template" },
    mutationFn: async (template: Partial<FormTemplate>) => {
      const { data } = await api.post("/admin/form-templates", template);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "form-templates"] });
    },
  });
}

/**
 * Update form template
 */
export function useUpdateFormTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormBuilderPage catch is sole caller.
    meta: { errorFallback: "Failed to save template" },
    mutationFn: async (updates: Partial<FormTemplate>) => {
      const { data } = await api.put(`/admin/form-templates/${id}`, updates);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "form-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "form-templates", id],
      });
    },
  });
}

/**
 * Publish a draft form template (draft → published)
 */
export function usePublishFormTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormBuilderPage catch is sole caller.
    meta: { errorFallback: "Failed to publish template" },
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/form-templates/${id}/publish`);
      return data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "form-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "form-templates", id],
      });
    },
  });
}

/**
 * Delete (archive) form template
 */
export function useDeleteFormTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormTemplatesPage/FormTemplateDetailPage catch is sole caller.
    meta: { errorFallback: "Failed to delete template" },
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/admin/form-templates/${id}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "form-templates"] });
    },
  });
}

/**
 * Get form template assignments (group homes and facilities)
 */
export function useFormTemplateAssignments(templateId: string) {
  return useQuery({
    queryKey: ["form-templates", templateId, "assignments"],
    queryFn: async () => {
      const { data } = await api.get(
        `/form-templates/${templateId}/assignments`,
      );
      return data.data;
    },
    enabled: !!templateId,
  });
}

export interface FormTemplateVersion extends FormTemplate {
  form_template_facilities: Array<{
    id: string;
    facility_id: string;
    assigned_at: string;
    facilities: { id: string; name: string };
  }>;
  form_template_group_homes: Array<{
    id: string;
    group_home_id: string;
    assigned_at: string;
    group_homes: { id: string; name: string };
  }>;
}

/**
 * Fetch all versions of a template chain (including assignments per version)
 */
export function useFormTemplateVersions(templateId: string | null) {
  return useQuery<FormTemplateVersion[]>({
    queryKey: ["admin", "form-templates", templateId, "versions"],
    queryFn: async () => {
      const { data } = await api.get(
        `/admin/form-templates/${templateId}/versions`,
      );
      return data.data;
    },
    enabled: !!templateId,
  });
}

/**
 * Assign form template to group homes and/or facilities
 */
export function useAssignFormTemplate(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormAssignmentModal catch is sole caller.
    meta: { errorFallback: "Failed to update assignments" },
    mutationFn: async (payload: {
      group_home_ids?: string[];
      facility_ids?: string[];
    }) => {
      const { data } = await api.post(
        `/form-templates/${templateId}/assignments`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["form-templates", templateId, "assignments"],
      });
      queryClient.invalidateQueries({ queryKey: ["form-templates", "usable"] });
    },
  });
}

/**
 * Remove form template assignment
 */
export function useUnassignFormTemplate(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FormAssignmentModal catch is sole caller.
    meta: { errorFallback: "Failed to update assignments" },
    mutationFn: async (params: {
      group_home_id?: string;
      facility_id?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params.group_home_id)
        queryParams.append("group_home_id", params.group_home_id);
      if (params.facility_id)
        queryParams.append("facility_id", params.facility_id);

      const { data } = await api.delete(
        `/form-templates/${templateId}/assignments?${queryParams.toString()}`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["form-templates", templateId, "assignments"],
      });
      queryClient.invalidateQueries({ queryKey: ["form-templates", "usable"] });
    },
  });
}
